import { AnimatePresence } from "framer-motion";
import { useEffect, useState, type ReactElement } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { MapboxBackground } from "./map/components/MapboxBackground";
import { MapProvider, useMapContext } from "./map/contexts/MapContext";
import { AppErrorBoundary } from "./shared/components/ErrorBoundary";
import { Toaster } from "./shared/components/toaster";
import { useAutoUpdaterNotifications } from "./shared/hooks/useAutoUpdaterNotifications";
import { useElectronMenu } from "./shared/hooks/useElectronMenu";
import { NavigationTracker } from "./telemetry/NavigationTracker";

// Feature flag for Mapbox integration
const USE_MAPBOX = import.meta.env.VITE_USE_MAPBOX_MAP === "true";

/**
 * Root layout component that provides AnimatePresence for all page transitions.
 * Also renders the Mapbox map as a fixed background layer when enabled.
 *
 * The map is rendered here (not in individual pages) to:
 * - Avoid DOM moves between pages (better performance, no resize issues)
 * - Provide a stable container for the singleton map instance
 * - Let pages "show through" to the map background
 *
 * Pages can control the map by calling setMapContext from useMapContext hook.
 */
export function RootLayout(): ReactElement {
  const location = useLocation();

  return (
    <AppErrorBoundary>
      <MapProvider>
        <RootLayoutContent location={location} />
      </MapProvider>
    </AppErrorBoundary>
  );
}

function RootLayoutContent({ location }: { readonly location: ReturnType<typeof useLocation> }): ReactElement {
  const mapContext = useMapContext();

  // Handle Electron menu commands (navigation and playback)
  useElectronMenu();

  // Listen for Electron auto-updater events and show toast notifications
  useAutoUpdaterNotifications();

  // Determine if map should be visible on current route
  const showMap =
    location.pathname === "/home" ||
    location.pathname.startsWith("/home/browse") ||
    location.pathname.startsWith("/home/title") ||
    location.pathname === "/sign-in" ||
    location.pathname === "/sign-up";

  // Show blur card on signin page
  const isSignInPage = location.pathname === "/sign-in";

  // Blur card state with opacity control for manual fade
  const [showBlurCard, setShowBlurCard] = useState(isSignInPage);
  const [blurCardOpacity, setBlurCardOpacity] = useState(isSignInPage ? 1 : 0);

  // Listen for signin success event to trigger fade
  useEffect(() => {
    const handleSigninSuccess = () => {
      setBlurCardOpacity(0);
      // Remove after fade completes
      setTimeout(() => setShowBlurCard(false), 1700);
    };

    globalThis.addEventListener("signin-success", handleSigninSuccess);
    return () => globalThis.removeEventListener("signin-success", handleSigninSuccess);
  }, []);

  useEffect(() => {
    if (isSignInPage) {
      // Entering signin page - show immediately
      setShowBlurCard(true);
      setBlurCardOpacity(1);
    } else if (!isSignInPage && showBlurCard && blurCardOpacity === 1) {
      // Left signin page without signin event (e.g., back button) - clean up
      setShowBlurCard(false);
    }
  }, [isSignInPage, showBlurCard, blurCardOpacity]);

  // On browse pages, map needs to be interactive, so content layer needs pointer-events: none
  const isBrowsePage = location.pathname.startsWith("/home/browse");

  return (
    <div className="min-h-dvh bg-black text-white overflow-x-hidden" data-lumi-app>
      {/* Mapbox background - fixed position, behind all content */}
      {/* Always render when feature flag is on to avoid unmount/remount */}
      {USE_MAPBOX && (
        <div className={`fixed inset-0 z-0 ${showMap ? "" : "hidden"}`}>
          <MapboxBackground
            countryCounts={mapContext.countryCounts}
            selectedCountry={mapContext.selectedCountry}
            selectedTitleId={mapContext.selectedTitleId}
            onCountryClick={mapContext.onCountryClick}
            onTitleClick={mapContext.onTitleClick}
            blurAmount={mapContext.blurAmount}
            markerTitles={mapContext.markerTitles}
            markerFilterSummary={mapContext.markerFilterSummary}
            className="w-full h-full"
          />
        </div>
      )}

      {/* Content layer - renders over the map */}
      {/* On browse pages, use pointer-events-none so map interactions work */}
      <div className={`relative z-10 ${isBrowsePage ? "pointer-events-none" : ""}`}>
        <NavigationTracker />
        <AnimatePresence mode="wait" initial={false}>
          <Outlet key={location.pathname} />
        </AnimatePresence>
      </div>

      {/* Toast notifications - rendered at root level for all pages */}
      <Toaster />
    </div>
  );
}
